import React, { useState } from 'react';
import axios from 'axios';
import PublicLayout from '@/components/PublicLayout';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeleteAccountPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    role: 'agent',
    reason: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Assuming you have a contact or support endpoint to handle this
      // If you don't have this specific endpoint yet, you can route it to your standard contact form API
      await axios.post(`${API}/contact`, {
        name: formData.fullName,
        email: formData.email,
        subject: `ACCOUNT DELETION REQUEST: ${formData.role.toUpperCase()}`,
        message: `Role: ${formData.role}\nReason: ${formData.reason}\n\nPlease delete my account and associated data from the Albriks platform.`
      });
      
      setSubmitted(true);
      toast.success('Deletion request submitted successfully.');
    } catch (error) {
      console.error('Deletion request error:', error);
      toast.error('Failed to submit request. Please email support@albriks.com directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-[#1A4D2E] mb-4" style={{fontFamily: '"Playfair Display", serif'}}>
            Request Account Deletion
          </h1>
          <p className="text-stone-600 text-lg">
            Submit a request to permanently delete your Albriks account and associated data.
          </p>
        </div>
        
        <Card className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-stone-100">
          {submitted ? (
            <div className="text-center py-8">
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-stone-800 mb-2">Request Received</h2>
              <p className="text-stone-600 mb-6">
                Your account deletion request has been forwarded to the administrative team. We will process the removal of your data and notify you at <strong>{formData.email}</strong> once complete.
              </p>
              <button 
                onClick={() => window.location.href = '/'}
                className="bg-[#1A4D2E] text-white px-6 py-2 rounded-lg font-medium hover:bg-[#133821] transition-colors"
              >
                Return to Home
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-4 p-4 mb-8 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
                <AlertTriangle className="h-6 w-6 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold mb-1">Important Information</p>
                  <p>Because Albriks is an enterprise platform, your account is linked to a brokerage. Deleting your account will remove your access to all leads, assignments, and open house data. This action cannot be undone.</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-stone-700">Full Name</label>
                    <input 
                      type="text" 
                      name="fullName"
                      required
                      value={formData.fullName}
                      onChange={handleChange}
                      className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] outline-none transition-all"
                      placeholder="John Doe"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-stone-700">Registered Email</label>
                    <input 
                      type="email" 
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] outline-none transition-all"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-stone-700">Account Type</label>
                  <select 
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] outline-none transition-all bg-white"
                  >
                    <option value="agent">Real Estate Agent</option>
                    <option value="broker">Broker / Admin</option>
                    <option value="provider">Service Provider</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-stone-700">Reason for Deletion (Optional)</label>
                  <textarea 
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    rows="4"
                    className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] outline-none transition-all resize-none"
                    placeholder="Please let us know why you are leaving..."
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-red-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-70 flex justify-center items-center"
                >
                  {loading ? 'Submitting Request...' : 'Submit Deletion Request'}
                </button>
              </form>
            </>
          )}
        </Card>
      </div>
    </PublicLayout>
  );
};

export default DeleteAccountPage;
